import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';

const AttendanceManagement = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [instructors, setInstructors] = useState([]);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    instructorId: '',
    status: '',
    includeSpecialRequests: true,
    includeContactInfo: true,
  });
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // States for the summary display
  const [summaryData, setSummaryData] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [summaryError, setSummaryError] = useState('');
  const [chartProcessing, setChartProcessing] = useState(false);
  
  // States for table pagination
  const [instructorTableLimit, setInstructorTableLimit] = useState(10);
  const [classTableLimit, setClassTableLimit] = useState(10);
  const [loadingMoreInstructors, setLoadingMoreInstructors] = useState(false);
  const [loadingMoreClasses, setLoadingMoreClasses] = useState(false);

  // Fetch instructors when component mounts
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Set default date range (last 30 days)
        const today = new Date();
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(today.getDate() - 30);
        
        setFilters(prev => ({
          ...prev,
          startDate: thirtyDaysAgo.toISOString().split('T')[0],
          endDate: today.toISOString().split('T')[0]
        }));

        // Fetch instructors
        const instructorsResponse = await axios.get('instructors');
        setInstructors(instructorsResponse.data.filter(instructor => instructor.status === 'active'));
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(t('error_fetching_data'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [t]);

  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear summary data when filters change
    setSummaryData(null);
  };

  const generateExcelReport = async () => {
    setGenerating(true);
    setError('');
    setSuccess('');

    try {
      // Make API call to generate Excel report
      const response = await axios.post(
        'attendance/admin/attendance/export', 
        filters,
        { responseType: 'blob' } // Important for file download
      );
      
      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Create filename with current date
      const date = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `attendance_report_${date}.xlsx`);
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      setSuccess(t('report_generated_successfully'));
    } catch (err) {
      console.error('Error generating report:', err);
      // Check for permission denied error (403)
      if (err.response && err.response.status === 403) {
        setError(t('attendance_access_denied'));
      } else {
        setError(t('error_generating_report'));
      }
    } finally {
      setGenerating(false);
    }
  };
  
  const fetchAttendanceSummary = async () => {
    setLoadingSummary(true);
    setSummaryError('');
    setSummaryData(null);
    setChartProcessing(false);
    
    try {
      const response = await axios.post('attendance/admin/attendance/summary', filters);
      
      // For large datasets, set chart processing state
      if (response.data.classBreakdown && response.data.classBreakdown.length > 50) {
        setChartProcessing(true);
        // Use setTimeout to allow the UI to update before heavy processing
        setTimeout(() => {
          setSummaryData(response.data);
          setChartProcessing(false);
        }, 100);
      } else {
        setSummaryData(response.data);
      }
    } catch (err) {
      console.error('Error fetching attendance summary:', err);
      // Check for permission denied error (403)
      if (err.response && err.response.status === 403) {
        setSummaryError(t('attendance_access_denied'));
      } else {
        setSummaryError(t('error_fetching_summary'));
      }
    } finally {
      setLoadingSummary(false);
    }
  };
  
  // Format number with commas as thousands separators
  const formatNumber = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };
  
  // Prepare data for the attendance chart
  const prepareChartData = () => {
    if (!summaryData || !summaryData.classBreakdown || summaryData.classBreakdown.length === 0) {
      return [];
    }
    
    // Sort classes by date
    const sortedClasses = [...summaryData.classBreakdown].sort((a, b) => {
      return new Date(a.date) - new Date(b.date);
    });
    
    // Group classes by date for the chart
    const dataByDate = {};
    
    sortedClasses.forEach(cls => {
      const dateObj = new Date(cls.date);
      // Format date as YYYY-MM-DD for consistent grouping
      const dateStr = dateObj.toISOString().split('T')[0];
      
      if (!dataByDate[dateStr]) {
        dataByDate[dateStr] = {
          date: dateStr,
          displayDate: formatDateForDisplay(dateObj),
          total: 0,
          instructors: {}
        };
      }
      
      // Add to total count
      dataByDate[dateStr].total += cls.studentCount;
      
      // Group by instructor
      if (!dataByDate[dateStr].instructors[cls.instructor]) {
        dataByDate[dateStr].instructors[cls.instructor] = 0;
      }
      dataByDate[dateStr].instructors[cls.instructor] += cls.studentCount;
    });
    
    // Convert to array and flatten instructor data
    const chartData = Object.values(dataByDate);
    
    // Get unique instructors
    const uniqueInstructors = new Set();
    sortedClasses.forEach(cls => uniqueInstructors.add(cls.instructor));
    
    // Ensure all instructor fields exist in each data point
    chartData.forEach(dataPoint => {
      uniqueInstructors.forEach(instructor => {
        if (!dataPoint.instructors[instructor]) {
          dataPoint.instructors[instructor] = 0;
        }
        // Add instructor data as direct properties
        dataPoint[instructor] = dataPoint.instructors[instructor];
      });
    });
    
    // If there are too many data points, aggregate them further
    if (chartData.length > 100) {
      return aggregateDataPoints(chartData, uniqueInstructors);
    }
    
    return chartData;
  };
  
  // Aggregate data points for very large datasets
  const aggregateDataPoints = (data, uniqueInstructors) => {
    // Get date range in days
    const startDate = new Date(filters.startDate);
    const endDate = new Date(filters.endDate);
    const rangeDays = Math.round((endDate - startDate) / (1000 * 60 * 60 * 24));
    
    let aggregationUnit = 'day';
    let aggregationFormat = { year: 'numeric', month: '2-digit', day: '2-digit' };
    
    // For very large ranges, aggregate by month or year
    if (rangeDays > 365) {
      aggregationUnit = 'month';
      aggregationFormat = { year: 'numeric', month: 'short' };
    } else if (rangeDays > 60) {
      aggregationUnit = 'week';
      aggregationFormat = { year: 'numeric', month: 'short', day: '2-digit' };
    }
    
    // Group data by the chosen aggregation unit
    const aggregatedData = {};
    
    data.forEach(point => {
      const date = new Date(point.date);
      let key;
      
      if (aggregationUnit === 'month') {
        key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      } else if (aggregationUnit === 'week') {
        // Get the week number (approximate)
        const weekNumber = Math.ceil((date.getDate() + new Date(date.getFullYear(), date.getMonth(), 1).getDay()) / 7);
        key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-W${weekNumber}`;
      } else {
        key = point.date;
      }
      
      if (!aggregatedData[key]) {
        aggregatedData[key] = {
          date: key,
          displayDate: date.toLocaleDateString(undefined, aggregationFormat),
          total: 0,
          instructors: {},
          count: 0 // To calculate averages
        };
        
        // Initialize instructor counts
        Array.from(uniqueInstructors).forEach(instructor => {
          aggregatedData[key].instructors[instructor] = 0;
        });
      }
      
      // Add data
      aggregatedData[key].total += point.total;
      aggregatedData[key].count += 1;
      
      // Add instructor data
      Array.from(uniqueInstructors).forEach(instructor => {
        if (point[instructor]) {
          aggregatedData[key].instructors[instructor] += point[instructor];
        }
      });
    });
    
    // Convert to array and flatten instructor data
    const result = Object.values(aggregatedData).map(point => {
      // Add instructor data as direct properties
      Array.from(uniqueInstructors).forEach(instructor => {
        point[instructor] = point.instructors[instructor];
      });
      
      return point;
    });
    
    // Sort by date
    result.sort((a, b) => {
      return a.date.localeCompare(b.date);
    });
    
    return result;
  };
  
  // Format date for display based on the date range
  const formatDateForDisplay = (dateObj) => {
    // Get the date range in days
    const startDate = new Date(filters.startDate);
    const endDate = new Date(filters.endDate);
    const rangeDays = Math.round((endDate - startDate) / (1000 * 60 * 60 * 24));
    
    // For ranges more than 90 days, show only month and year
    if (rangeDays > 90) {
      return dateObj.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
    }
    // For ranges more than 14 days but less than 90, show day and month
    else if (rangeDays > 14) {
      return dateObj.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
    }
    // For short ranges, show full date
    else {
      return dateObj.toLocaleDateString();
    }
  };
  
  // Determine if we should show all ticks on x-axis
  const shouldShowAllTicks = () => {
    if (!summaryData || !summaryData.classBreakdown) return true;
    
    // Get the date range in days
    const startDate = new Date(filters.startDate);
    const endDate = new Date(filters.endDate);
    const rangeDays = Math.round((endDate - startDate) / (1000 * 60 * 60 * 24));
    
    // For large ranges, don't show all ticks
    return rangeDays <= 14;
  };
  
  // Determine interval for x-axis ticks
  const getTickInterval = () => {
    if (!summaryData || !summaryData.classBreakdown) return 0;
    
    const chartData = prepareChartData();
    const dataLength = chartData.length;
    
    // Show at most 12 ticks on the x-axis
    if (dataLength <= 12) return 0; // Show all ticks
    
    return Math.ceil(dataLength / 12);
  };
  
  // Format x-axis tick
  const formatXAxisTick = (value) => {
    // Get the date range in days
    const startDate = new Date(filters.startDate);
    const endDate = new Date(filters.endDate);
    const rangeDays = Math.round((endDate - startDate) / (1000 * 60 * 60 * 24));
    
    const date = new Date(value);
    
    // For ranges more than 365 days, show only year
    if (rangeDays > 365) {
      return date.getFullYear();
    }
    // For ranges more than 90 days, show only month and year
    else if (rangeDays > 90) {
      return date.toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
    }
    // For ranges more than 30 days, show only day and month
    else if (rangeDays > 30) {
      return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
    }
    // For short ranges, show full date
    else {
      return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
    }
  };
  
  // Get unique instructors for chart legend
  const getUniqueInstructors = () => {
    if (!summaryData || !summaryData.classBreakdown) return [];
    
    const uniqueInstructors = new Set();
    summaryData.classBreakdown.forEach(cls => uniqueInstructors.add(cls.instructor));
    return Array.from(uniqueInstructors);
  };
  
  // Generate a color for each instructor
  const getInstructorColor = (index) => {
    const colors = ['#3498DB', '#2ECC71', '#E74C3C', '#F39C12', '#9B59B6', '#1ABC9C', '#D35400', '#34495E'];
    return colors[index % colors.length];
  };

  // Function to load more instructor rows
  const loadMoreInstructors = () => {
    setLoadingMoreInstructors(true);
    // Use setTimeout to prevent UI freezing
    setTimeout(() => {
      setInstructorTableLimit(prev => prev + 10);
      setLoadingMoreInstructors(false);
    }, 100);
  };
  
  // Function to load more class rows
  const loadMoreClasses = () => {
    setLoadingMoreClasses(true);
    // Use setTimeout to prevent UI freezing
    setTimeout(() => {
      setClassTableLimit(prev => prev + 20);
      setLoadingMoreClasses(false);
    }, 100);
  };
  
  // Reset pagination limits when new data is loaded
  useEffect(() => {
    if (summaryData) {
      setInstructorTableLimit(10);
      setClassTableLimit(10);
    }
  }, [summaryData]);

  return (
    <div className="attendance-management">
      <h2>{t('attendance_management')}</h2>
      
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}
      
      <div className="filters-section">
        <h3>{t('filter_options')}</h3>
        
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="startDate">{t('start_date')}</label>
            <input
              type="date"
              id="startDate"
              name="startDate"
              value={filters.startDate}
              onChange={handleFilterChange}
              className="form-control"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="endDate">{t('end_date')}</label>
            <input
              type="date"
              id="endDate"
              name="endDate"
              value={filters.endDate}
              onChange={handleFilterChange}
              className="form-control"
            />
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="instructorId">{t('instructor')}</label>
            <select
              id="instructorId"
              name="instructorId"
              value={filters.instructorId}
              onChange={handleFilterChange}
              className="form-control"
            >
              <option value="">{t('all_instructors')}</option>
              {instructors.map(instructor => (
                <option key={instructor.id} value={instructor.id}>
                  {instructor.name}
                </option>
              ))}
            </select>
          </div>
          
          <div className="form-group">
            <label htmlFor="status">{t('booking_status')}</label>
            <select
              id="status"
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              className="form-control"
            >
              <option value="">{t('all_statuses')}</option>
              <option value="confirmed">{t('confirmed')}</option>
              <option value="cancelled">{t('cancelled')}</option>
              <option value="no-show">{t('no_show') || 'No Show'}</option>
            </select>
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-group checkbox-group">
            <input
              type="checkbox"
              id="includeSpecialRequests"
              name="includeSpecialRequests"
              checked={filters.includeSpecialRequests}
              onChange={handleFilterChange}
            />
            <label htmlFor="includeSpecialRequests">
              {t('include_special_requests')}
            </label>
          </div>
          
          <div className="form-group checkbox-group">
            <input
              type="checkbox"
              id="includeContactInfo"
              name="includeContactInfo"
              checked={filters.includeContactInfo}
              onChange={handleFilterChange}
            />
            <label htmlFor="includeContactInfo">
              {t('include_contact_info')}
            </label>
          </div>
        </div>
      </div>
      
      <div className="action-buttons">
        <button 
          className="display-summary-button"
          onClick={fetchAttendanceSummary}
          disabled={loadingSummary || loading}
        >
          {loadingSummary ? t('loading_summary') : t('display_attendance')}
        </button>
        
        <button 
          className="generate-report-button"
          onClick={generateExcelReport}
          disabled={generating || loading}
        >
          {generating ? t('generating_report') : t('generate_excel_report')}
        </button>
      </div>
      
      {/* Summary Display Section */}
      {summaryError && <div className="error-message">{summaryError}</div>}
      
      {summaryData && (
        <div className="summary-section">
          <h3>{t('attendance_summary')}</h3>
          
          <div className="summary-period">
            <span className="period-label">{t('period')}:</span> 
            <span className="period-value">
              {new Date(filters.startDate).toLocaleDateString()} - {new Date(filters.endDate).toLocaleDateString()}
            </span>
          </div>
          
          <div className="summary-stats">
            <div className="stat-card">
              <div className="stat-value">{formatNumber(summaryData.totalClasses)}</div>
              <div className="stat-label">{t('total_classes')}</div>
            </div>
            
            <div className="stat-card">
              <div className="stat-value">{formatNumber(summaryData.totalStudents)}</div>
              <div className="stat-label">{t('total_students')}</div>
            </div>
            
            <div className="stat-card">
              <div className="stat-value">{summaryData.averageAttendance.toFixed(1)}</div>
              <div className="stat-label">{t('average_per_class')}</div>
            </div>
          </div>
          
          {summaryData.instructorStats && summaryData.instructorStats.length > 0 && (
            <div className="instructor-stats">
              <h4>{t('instructor_breakdown')}</h4>
              <div className="instructor-stats-table-container">
                <table className="instructor-stats-table">
                  <thead>
                    <tr>
                      <th>{t('instructor')}</th>
                      <th>{t('classes_taught')}</th>
                      <th>{t('total_students')}</th>
                      <th>{t('average_attendance')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summaryData.instructorStats
                      .slice(0, instructorTableLimit)
                      .map((instructor, index) => (
                        <tr key={index}>
                          <td>{instructor.name}</td>
                          <td>{instructor.classCount}</td>
                          <td>{instructor.studentCount}</td>
                          <td>{instructor.averageAttendance.toFixed(1)}</td>
                        </tr>
                    ))}
                  </tbody>
                </table>
                
                {summaryData.instructorStats.length > instructorTableLimit && (
                  <div className="see-more-container">
                    <button 
                      className="see-more-button"
                      onClick={loadMoreInstructors}
                      disabled={loadingMoreInstructors}
                    >
                      {loadingMoreInstructors ? t('loading') : t('see_more')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {summaryData.classBreakdown && summaryData.classBreakdown.length > 0 && (
            <div className="class-breakdown">
              <h4>{t('class_breakdown')}</h4>
              <div className="class-breakdown-table-container">
                <table className="class-breakdown-table">
                  <thead>
                    <tr>
                      <th>{t('class_name')}</th>
                      <th>{t('date')}</th>
                      <th>{t('instructor')}</th>
                      <th>{t('students')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summaryData.classBreakdown
                      .slice(0, classTableLimit)
                      .map((cls, index) => (
                        <tr key={index}>
                          <td>{cls.name}</td>
                          <td>{new Date(cls.date).toLocaleDateString()}</td>
                          <td>{cls.instructor}</td>
                          <td>{cls.studentCount}</td>
                        </tr>
                    ))}
                  </tbody>
                </table>
                
                {summaryData.classBreakdown.length > classTableLimit && (
                  <div className="see-more-container">
                    <button 
                      className="see-more-button"
                      onClick={loadMoreClasses}
                      disabled={loadingMoreClasses}
                    >
                      {loadingMoreClasses ? t('loading') : t('see_more')}
                    </button>
                    <div className="entries-info">
                      {t('showing')} {Math.min(classTableLimit, summaryData.classBreakdown.length)} {t('of')} {summaryData.classBreakdown.length} {t('entries')}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Attendance Chart */}
              <div className="attendance-chart-container">
                <h4>{t('attendance_trend')}</h4>
                
                {chartProcessing ? (
                  <div className="chart-loading">
                    <div className="spinner"></div>
                    <p>{t('processing_chart_data')}</p>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart
                      data={prepareChartData()}
                      margin={{ top: 5, right: 30, left: 20, bottom: 30 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="date" 
                        label={{ 
                          value: t('date'), 
                          position: 'insideBottomRight', 
                          offset: -10 
                        }}
                        interval={getTickInterval()}
                        tickFormatter={formatXAxisTick}
                        tick={{ fontSize: 11 }}
                        angle={-45}
                        textAnchor="end"
                        height={60}
                      />
                      <YAxis 
                        label={{ 
                          value: t('students'), 
                          angle: -90, 
                          position: 'insideLeft' 
                        }} 
                      />
                      <Tooltip 
                        formatter={(value, name) => {
                          if (name === 'total') return [value, t('total_students')];
                          return [value, name];
                        }}
                        labelFormatter={(label) => {
                          const dataPoint = prepareChartData().find(point => point.date === label);
                          return dataPoint ? dataPoint.displayDate : label;
                        }}
                      />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="total" 
                        name={t('total_students')}
                        stroke="#34495E" 
                        strokeWidth={2}
                        dot={{ r: 4 }}
                        activeDot={{ r: 6 }}
                      />
                      {getUniqueInstructors().map((instructor, index) => (
                        <Line 
                          key={instructor}
                          type="monotone" 
                          dataKey={instructor} 
                          name={instructor}
                          stroke={getInstructorColor(index)} 
                          strokeWidth={1.5}
                          dot={{ r: 3 }}
                          activeDot={{ r: 5 }}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          )}
        </div>
      )}
      
      <div className="export-hint">
        <p>{t('export_hint')}</p>
      </div>
    </div>
  );
};

export default AttendanceManagement; 